import React from "react";
import { motion } from "framer-motion";
import {
  Target,
  Award,
  Globe,
  Users,
  Sparkles,
} from "lucide-react";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  BlueprintCard,
  PrimaryButton,
  motionFadeUp,
  C,
} from "../../design-system";
import { Link } from "react-router-dom";
import { createPageUrl } from "../../utils";

/* ------------------------------------------------------------------ */
/*  CONTENT                                                            */
/* ------------------------------------------------------------------ */
const values = [
  {
    icon: Target,
    title: "Innovation first",
    description:
      "Cutting-edge AI baked into every workflow not bolted on as a feature.",
    accent: C.coral,
  },
  {
    icon: Users,
    title: "People-centric",
    description:
      "Tools that empower both candidates and hiring teams, equally.",
    accent: C.mint,
  },
  {
    icon: Globe,
    title: "Global by design",
    description:
      "Multi-country compliance and localized workflows from day one.",
    accent: C.gold,
  },
  {
    icon: Award,
    title: "Excellence",
    description:
      "Quality you can audit code, data and the people we hire to build it.",
    accent: C.coral,
  },
];

const stats = [
  { value: "2024", label: "Founded" },
  { value: "5",    label: "Countries" },
  { value: "50K+", label: "Active users" },
  { value: "5K+",  label: "Companies" },
];

const principles = [
  { idx: "01", title: "Be honest about AI",     desc: "Show what is automated and where humans decide." },
  { idx: "02", title: "Reduce ceremony",        desc: "Workflows should feel like one good idea, not ten." },
  { idx: "03", title: "Respect candidate time", desc: "Faster updates, fewer dead ends, transparent next steps." },
  { idx: "04", title: "Ship to learn",          desc: "Small releases over polished announcements." },
];

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */
export default function About() {
  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section
        className="relative pt-16 pb-20 md:pt-24 md:pb-28 border-b ds-bg-spotlight"
        style={{ borderColor: C.hairline }}
      >
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>About · WebIdeal</StatusBadge>
            </motion.div>

            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5.5rem] xl:text-[6.5rem] font-extrabold leading-[0.92] tracking-tight max-w-5xl"
              style={{ color: C.forest }}
            >
              Hiring should feel
              <br />
              <span style={{ color: `${C.forest}80` }}>obvious, not heavy.</span>
            </motion.h1>

            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-3xl"
              style={{ color: C.grid }}
            >
              We build the unified hiring platform that pre-AI tools wish they
              were one record across ATS, CRM, HRMS and the job portal,
              orchestrated by AI agents that quietly do the boring work.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* MISSION / VISION */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="grid md:grid-cols-2 gap-px" style={{ background: C.hairline }}>
            <BlueprintCard accent={C.coral} className="md:p-12">
              <TechLabel style={{ color: C.coral }}>Mission</TechLabel>
              <h2
                className="mt-3 text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight"
                style={{ color: C.forest }}
              >
                Make hiring smart, fair and fast for everyone in the loop.
              </h2>
              <p className="mt-5 leading-relaxed" style={{ color: C.grid }}>
                Replace fragmented tooling with one calm, AI-orchestrated workflow
                that respects candidates and gives recruiters their week back.
              </p>
            </BlueprintCard>

            <BlueprintCard accent={C.mint} className="md:p-12">
              <TechLabel style={{ color: "#3a6b4d" }}>Vision</TechLabel>
              <h2
                className="mt-3 text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight"
                style={{ color: C.forest }}
              >
                The default operating system for talent globally, by 2030.
              </h2>
              <p className="mt-5 leading-relaxed" style={{ color: C.grid }}>
                One platform across countries and roles, with the AI horsepower of
                an enterprise vendor and the simplicity of a single tool.
              </p>
            </BlueprintCard>
          </div>
        </PageContainer>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <StatusBadge>What we believe</StatusBadge>
              <h2
                className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight"
                style={{ color: C.forest }}
              >
                Four values, applied daily.
              </h2>
            </div>
            <TechLabel style={{ color: `${C.grid}99` }}>
              culture · validated
            </TechLabel>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px"
            style={{ background: C.hairline }}
          >
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
              >
                <BlueprintCard accent={v.accent} spatial className="h-full hover:bg-white">
                  <div
                    className="w-11 h-11 flex items-center justify-center mb-5"
                    style={{ background: C.forest, color: C.paper }}
                  >
                    <v.icon className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-1 text-xl font-bold leading-tight" style={{ color: C.forest }}>
                    {v.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: C.grid }}>
                    {v.description}
                  </p>
                </BlueprintCard>
              </motion.div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* STATS */}
      <section
        className="py-20 md:py-24 border-b"
        style={{ borderColor: C.hairline, background: C.forest, color: C.paper }}
      >
        <PageContainer>
          <div className="mb-12 max-w-2xl">
            <StatusBadge color={C.mint}>By the numbers</StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight">
              Small team. Big footprint.
            </h2>
          </div>

          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-px"
            style={{ background: "rgba(158,255,191,0.20)" }}
          >
            {stats.map((s, i) => (
              <div
                key={s.label}
                className="p-7 md:p-9 flex flex-col gap-2"
                style={{ background: C.forest }}
              >
                <span
                  className="text-4xl md:text-5xl font-extrabold tracking-tight tabular-nums"
                  style={{ color: C.paper }}
                >
                  {s.value}
                </span>
                <TechLabel style={{ color: "rgba(247,247,245,0.55)" }}>
                  {s.label}
                </TechLabel>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* PRINCIPLES */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-12 max-w-2xl">
            <StatusBadge>Principles</StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
              How we build.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-px" style={{ background: C.hairline }}>
            {principles.map((p) => (
              <div
                key={p.idx}
                className="p-7 md:p-8 flex items-start gap-5"
                style={{ background: C.paper }}
              >
                <span
                  className="text-2xl md:text-3xl font-extrabold tabular-nums shrink-0"
                  style={{ color: `${C.forest}80`, letterSpacing: "0.02em" }}
                >
                  {p.idx}
                </span>
                <div>
                  <h3 className="text-xl font-bold leading-tight" style={{ color: C.forest }}>
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: C.grid }}>
                    {p.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24">
        <PageContainer>
          <div
            className="relative border p-10 md:p-14 text-center"
            style={{ borderColor: C.hairline, background: C.paper }}
          >
            <CornerMarkers color={C.forest} />
            <div className="flex justify-center mb-5">
              <StatusBadge>Eye · Looking ahead</StatusBadge>
            </div>
            <h2
              className="text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight"
              style={{ color: C.forest }}
            >
              Building hiring's quiet upgrade.
            </h2>
            <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto" style={{ color: C.grid }}>
              We'd rather be the platform every team forgets they're using because
              it just works than the one with the loudest landing page.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to={createPageUrl("Contact")}>
                <PrimaryButton>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Talk to us
                </PrimaryButton>
              </Link>
              <Link to={createPageUrl("Careers")}>
                <button
                  type="button"
                  className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center hover:bg-[#1A3C2B]/[0.04] transition-colors duration-150"
                  style={{ borderColor: C.forest, color: C.forest }}
                >
                  Join the team
                </button>
              </Link>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}
